import { SyntheticEvent, useEffect, useState, useRef } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Alert,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  SpaceBetween,
} from '@amzn/awsui-components-react';

import challengeMessages from '../i18n/answerChallenge.messages';
import {
  createCognitoUser,
  answerCustomChallenge,
  keepSessionOpen,
  startSession,
  getResponseUrl,
  globalSignOut,
} from '../services/emailOTPAuth';
import { UserAndAuthStatus } from '../shared/userAndAuthStatus.interface';
import { AppConfig } from '../types/app';
import logger from '../utils/logger';

import styles from './CardContainer.module.css';
import React from 'react';
import { useCountDown } from '../hooks/useCountDown';
import { rumService } from '../services/rum';

export const AnswerChallengeForm = ({
  config,
  headerMsg,
  descriptionMsg,
  showBackButton,
  useUser,
}: {
  config: AppConfig;
  headerMsg: MessageDescriptor;
  descriptionMsg: MessageDescriptor;
  showBackButton: boolean;
  useUser?: { userName: string; session: string; email: string };
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const hState = location.state;
  const errorMessage = formatMessage(challengeMessages.errorMessage);

  const refCognitoUser = useRef(undefined);
  let cognitoUserName = '';
  let cognitoSession = '';
  let userEmail = '';
  try {
    cognitoUserName = useUser
      ? useUser.userName
      : (hState as any).cognitoUserName;
    cognitoSession = useUser ? useUser.session : (hState as any).cognitoSession;
    userEmail = useUser ? useUser.email : (hState as any).userEmail;
  } catch (err) {
    throw new Error('No valid session.');
  }

  const keepAliveIntervalMs = 150000; // 2.5 minutes
  const keepAliveMaxAttempts = 6; // keep Cognito session alive for at least 15 minutes
  const resendCooldownSeconds = 30;
  const [keepAliveCount, setKeepAliveCount] = useState(0);
  const [keepAliveTimer, setKeepAliveTimer] = useState(0);

  const [loading, setLoading] = useState(false);
  const [otpEntered, setOtpEntered] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [passCodeError, setPassCodeError] = useState('');
  const [successMessages, setSuccessMessages] = useState(['']);

  const sessionDurationSeconds =
    keepAliveMaxAttempts * (keepAliveIntervalMs / 1000);
  const [timeLeft, resetTimer] = useCountDown(sessionDurationSeconds);
  const [resendCooldown, resetResendCooldown] = useCountDown(
    resendCooldownSeconds
  );

  const formatTimeLeft = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    if (timeLeft === 0) {
      stopKeepAlive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const successAlertBar = successMessages
    .filter((msg) => msg)
    .map((msg) => (
      <Alert
        key={msg}
        type="success"
        dismissible={true}
        onDismiss={() =>
          setSuccessMessages(
            successMessages.filter((innerMsg) => innerMsg !== msg)
          )
        }
      >
        {msg}
      </Alert>
    ));

  const isNineDigitCode = (input: string) => {
    const nineDigitRegExp = new RegExp('^[0-9]{9}$');
    return nineDigitRegExp.test(input);
  };

  const startKeepAlive = (reset = false) => {
    if (reset) {
      setKeepAliveCount(0);
    } else if (keepAliveCount >= keepAliveMaxAttempts) {
      return;
    }

    const timer = window.setInterval(async () => {
      try {
        refCognitoUser.current = (await keepSessionOpen(
          refCognitoUser.current
        )) as any;
      } catch (e) {
        // encountered an error; assume session has timed out
        stopKeepAlive();
      }

      setKeepAliveCount((prevCount) => prevCount + 1);
    }, keepAliveIntervalMs);
    setKeepAliveTimer(timer);
  };

  const stopKeepAlive = () => {
    window.clearInterval(keepAliveTimer);
  };

  const handleSendSecretCode = async (e?: CustomEvent) => {
    e?.preventDefault();

    if (!isNineDigitCode(otpValue)) {
      setPassCodeError(errorMessage);
      return;
    }
    try {
      setLoading(true);
      stopKeepAlive();
      const resultOfCustomChallenge: UserAndAuthStatus =
        await answerCustomChallenge(otpValue, refCognitoUser.current);
      refCognitoUser.current = resultOfCustomChallenge.cognitoUser as any;
      setLoading(false);
      if (resultOfCustomChallenge.isAuthenticated) {
        setLoading(true);
        setSuccessMessages(
          successMessages.concat(
            formatMessage(challengeMessages.successMessage)
          )
        );

        const urlParams = new URLSearchParams(location.search);
        const redirectUriParam = urlParams.get('redirect_uri');
        const stateParam = urlParams.get('state');
        const idToken = resultOfCustomChallenge.cognitoUser
          .getSignInUserSession()!
          .getIdToken()
          .getJwtToken();
        const accessToken = resultOfCustomChallenge.cognitoUser
          .getSignInUserSession()!
          .getAccessToken()
          .getJwtToken();

        const responseUrl = await getResponseUrl(
          config,
          idToken,
          accessToken,
          redirectUriParam!,
          stateParam!
        );
        await globalSignOut();
        window.location.assign(responseUrl);
      } else {
        rumService.recordEvent('otp_wrong_code_entered', { timeLeft });
        setPassCodeError(errorMessage);
        startKeepAlive();
      }
    } catch (err) {
      logger.error(err);
      rumService.recordEvent('otp_session_error', { timeLeft });
      resetTimer(0);
      resetResendCooldown(0);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    await handleSendSecretCode();
  };

  const resendPasscode = async () => {
    try {
      setLoading(true);
      stopKeepAlive();
      refCognitoUser.current = (await startSession(userEmail)) as any;
      startKeepAlive(true);
      resetTimer(sessionDurationSeconds);
      resetResendCooldown(resendCooldownSeconds);
      setLoading(false);
      setSuccessMessages(
        successMessages.concat(
          formatMessage(challengeMessages.resendMessage, { email: userEmail })
        )
      );
    } catch (err) {
      logger.error(err);
      resetTimer(0);
      resetResendCooldown(0);
      setLoading(false);
    }
  };

  const handleResendPasscode = async () => {
    await resendPasscode();
  };

  const handleCodeChange = (e: any) => {
    if (isNineDigitCode(e.detail.value)) {
      setOtpEntered(true);
      setPassCodeError('');
    } else {
      setOtpEntered(false);
    }
    setOtpValue(e.detail.value);
  };

  const getUser = async () => {
    refCognitoUser.current = (await getCognitoUser()) as any;
  };

  const getCognitoUser = async () => {
    if (cognitoUserName && cognitoSession) {
      const cognitoUser = await createCognitoUser(
        cognitoUserName,
        cognitoSession
      );
      return cognitoUser;
    } else {
      //TODO: User should not be clicking on button without a session
    }
  };

  useEffect(() => {
    // when component mounts, we must get the current Cognito user
    if (!refCognitoUser.current) {
      getUser();
    }

    if (keepAliveTimer === 0) {
      startKeepAlive();
    }

    if (keepAliveCount >= keepAliveMaxAttempts) {
      stopKeepAlive();
    }
  });

  return (
    <>
      <Container
        header={
          <Header
            className={styles.header}
            variant="h2"
            headingTagOverride="h1"
          >
            {formatMessage(headerMsg)}
          </Header>
        }
      >
        <form onSubmit={handleSubmit}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {showBackButton && (
                  <Button
                    data-testid="back-button"
                    onClick={() => {
                      stopKeepAlive();
                      navigate(-1);
                    }}
                    formAction="none"
                  >
                    {formatMessage(challengeMessages.previousButtonLabel)}
                  </Button>
                )}
                <Button
                  data-testid="submit-button"
                  variant="primary"
                  formAction="submit"
                  disabled={!otpEntered || timeLeft === 0}
                  loading={loading}
                >
                  {formatMessage(challengeMessages.buttonLabel)}
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween direction="vertical" size="s">
              <div>
                {formatMessage(descriptionMsg, {
                  userEmail: userEmail,
                })}
              </div>
              <FormField
                label={
                  <span>
                    {formatMessage(challengeMessages.passcodeInputName)}
                  </span>
                }
                constraintText={
                  <span
                    style={{
                      display: 'inline-block',
                      marginTop: '0.25em',
                    }}
                  >
                    {timeLeft > 0
                      ? formatMessage(challengeMessages.codeExpiresIn, {
                          time: formatTimeLeft(timeLeft),
                        })
                      : formatMessage(challengeMessages.codeExpired)}
                  </span>
                }
                errorText={passCodeError}
                stretch={true}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5em',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Input
                      name="secretCode"
                      type="text"
                      data-testid="text-input"
                      onChange={(event) => {
                        handleCodeChange(event);
                      }}
                      disabled={loading}
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus={true}
                      ariaRequired={true}
                      value={otpValue}
                    />
                  </div>
                  <Button
                    variant="normal"
                    formAction="none"
                    disabled={resendCooldown > 0}
                    onClick={(e) => {
                      e.preventDefault();
                      handleResendPasscode();
                    }}
                  >
                    {formatMessage(challengeMessages.resendPasscodeLabel)}
                  </Button>
                </div>
              </FormField>
            </SpaceBetween>
          </Form>
        </form>
      </Container>
      <div>{successAlertBar}</div>
    </>
  );
};
